import { X, Share2, Download } from "lucide-react";
import { toast } from "sonner";
import { useRef, useEffect, useState } from "react";
import { useAuth } from "@/lib/auth.jsx";

const COPY_FN = (name) => {
  const n = name ? `${name}, ` : "";
  return {
    first_100: {
      title: "First $100 Earned",
      line: `${n}first $100 outside the day job. Proof this works. Now we go for the next hundred.`,
      accent: "PROOF. LOCKED IN.",
      big: "$100",
    },
    first_1000: {
      title: "First $1,000 Earned",
      line: `${n}first grand. Outside the job. This is what building your way out looks like. Don't stop.`,
      accent: "THIS IS THE MOVE.",
      big: "$1,000",
    },
    streak_4: {
      title: "4 Week Streak",
      line: `${n}four weeks straight. Momentum is now a habit. This is the version of you that finishes what you start.`,
      accent: "MOMENTUM. UNLOCKED.",
      big: "4x",
    },
    streak_12: {
      title: "12 Week Streak",
      line: `${n}twelve weeks. Three months. Nobody who does this stops here. You are already out of the old story.`,
      accent: "UNSTOPPABLE.",
      big: "12x",
    },
  };
};

function drawCard(ctx, W, H, milestone, COPY) {
  const c = COPY[milestone];
  // Background
  ctx.fillStyle = "#0B0C0E";
  ctx.fillRect(0, 0, W, H);
  // Accent bar
  ctx.fillStyle = "#FD0F10";
  ctx.fillRect(0, 0, W, 8);
  // Grain-ish noise overlay (light)
  for (let i = 0; i < 500; i++) {
    ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.03})`;
    ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
  }
  // Big number
  ctx.fillStyle = "#FD0F10";
  ctx.font = "800 220px Outfit, sans-serif";
  ctx.textBaseline = "top";
  ctx.fillText(c.big, 80, 120);
  // Title
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "900 64px Outfit, sans-serif";
  ctx.fillText(c.title.toUpperCase(), 80, 380);
  // Line
  ctx.fillStyle = "#E5E7EB";
  ctx.font = "500 30px 'DM Sans', sans-serif";
  wrapText(ctx, c.line, 80, 500, W - 160, 42);
  // Accent tag
  ctx.fillStyle = "#FD0F10";
  ctx.font = "700 20px 'JetBrains Mono', monospace";
  ctx.fillText(c.accent, 80, H - 200);
  // Brand
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "900 32px Outfit, sans-serif";
  ctx.fillText("I HATE ", 80, H - 140);
  const w1 = ctx.measureText("I HATE ").width;
  ctx.fillStyle = "#FD0F10";
  ctx.fillText("MY JOB.", 80 + w1, H - 140);
  ctx.fillStyle = "#6B7280";
  ctx.font = "400 22px 'DM Sans', sans-serif";
  ctx.fillText("Built your way out, one week at a time.", 80, H - 90);
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(" ");
  let line = "";
  for (const w of words) {
    const test = line + w + " ";
    if (ctx.measureText(test).width > maxWidth && line !== "") {
      ctx.fillText(line.trim(), x, y);
      line = w + " ";
      y += lineHeight;
    } else {
      line = test;
    }
  }
  ctx.fillText(line.trim(), x, y);
}

export default function MilestoneModal({ milestone, onClose }) {
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);
  const COPY = COPY_FN(user?.name);

  useEffect(() => {
    if (!milestone || !COPY[milestone]) return;
    // Draw twice, small delay so custom fonts are ready
    const draw = () => {
      const cvs = canvasRef.current;
      if (!cvs) return;
      const ctx = cvs.getContext("2d");
      drawCard(ctx, cvs.width, cvs.height, milestone, COPY);
      setDataUrl(cvs.toDataURL("image/png"));
    };
    draw();
    const t = setTimeout(draw, 400);
    return () => clearTimeout(t);
  }, [milestone, user?.name]);

  if (!milestone || !COPY[milestone]) return null;
  const { title, line, accent } = COPY[milestone];

  const share = async () => {
    const text = `${line} (via I Hate My Job)`;
    const url = window.location.origin;
    if (navigator.share && dataUrl) {
      try {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], `${milestone}.png`, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ title, text, url, files: [file] });
          return;
        }
        await navigator.share({ title, text, url });
        return;
      } catch {}
    }
    navigator.clipboard.writeText(`${text}\n${url}`);
    toast.success("Copied. Paste it anywhere.");
  };

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `i-hate-my-job-${milestone}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Saved to your device.");
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-5" data-testid="milestone-modal">
      <div className="max-w-md w-full card-panel-elevated p-6 relative border-[#FD0F10]">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-white" aria-label="Close" data-testid="milestone-close">
          <X size={20}/>
        </button>
        <div className="eyebrow text-[#FD0F10]">MILESTONE UNLOCKED</div>
        <h2 className="font-display text-4xl sm:text-5xl font-black mt-2 leading-tight text-white" data-testid="milestone-title">{title}</h2>
        <div className="mt-3 font-mono-ui text-xs uppercase tracking-widest text-[#FD0F10]">{accent}</div>
        <p className="mt-4 text-gray-200 leading-relaxed">{line}</p>
        {dataUrl && (
          <div className="mt-5 rounded-lg overflow-hidden border border-[#2A2E39]" data-testid="milestone-card-preview">
            <img src={dataUrl} alt={`${title} card`} className="w-full block"/>
          </div>
        )}
        <canvas ref={canvasRef} width={1200} height={1200} className="hidden"/>
        <div className="mt-5 flex gap-2 flex-wrap">
          <button onClick={share} className="flex-1 hero-accent text-white font-bold py-3 rounded flex items-center justify-center gap-2" data-testid="milestone-share">
            <Share2 size={16}/> Share
          </button>
          <button onClick={download} className="flex-1 border border-[#FD0F10] text-[#FD0F10] font-bold py-3 rounded hover:bg-[#FD0F10] hover:text-white flex items-center justify-center gap-2" data-testid="milestone-download">
            <Download size={16}/> Download
          </button>
        </div>
        <button onClick={onClose} className="mt-3 w-full text-sm text-gray-400 hover:text-white" data-testid="milestone-keep-going">
          Keep going
        </button>
      </div>
    </div>
  );
}
